/**
 * 安全なLocalStorage → IndexedDB移行ユーティリティ
 * 
 * 設計原則：
 * 1. データ保護を最優先
 * 2. 段階的な移行（1ストアずつ）
 * 3. 完全なバックアップとロールバック機能
 * 4. 移行前後の検証
 */

import { indexedDBStorage } from './indexedDBAdapter'

export interface MigrationPlan {
  storeName: string
  localStorageKey: string
  currentDataSize: number
  backupCreated: boolean
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'rolled_back'
  error?: string
}

export interface StorageMigrationResult {
  success: boolean
  storeName: string
  dataIntegrity: boolean
  backupKey: string
  error?: string
}

export class SafeMigrationManager {
  private migrationPlans: Map<string, MigrationPlan> = new Map()
  private backupPrefix = 'migration_backup_'
  
  /**
   * 移行前の準備と検証
   */
  async prepareMigration(storeName: string): Promise<MigrationPlan> {
    const localStorageKey = storeName
    const localData = localStorage.getItem(localStorageKey)
    
    if (!localData) {
      throw new Error(`No data found for store: ${storeName}`)
    }
    
    const plan: MigrationPlan = {
      storeName,
      localStorageKey,
      currentDataSize: localData.length,
      backupCreated: false,
      status: 'pending'
    }
    
    // バックアップを作成
    const backupKey = `${this.backupPrefix}${storeName}_${Date.now()}`
    localStorage.setItem(backupKey, localData)
    plan.backupCreated = true
    
    this.migrationPlans.set(storeName, plan)
    
    console.log(`✅ Migration plan created for ${storeName}`)
    console.log(`   Data size: ${plan.currentDataSize} bytes`)
    console.log(`   Backup saved as: ${backupKey}`)
    
    return plan
  }
  
  /**
   * データの整合性を検証
   */
  private async verifyDataIntegrity(
    originalData: any,
    migratedData: any
  ): Promise<boolean> {
    try {
      // 基本的な構造比較
      const originalStr = JSON.stringify(originalData)
      const migratedStr = JSON.stringify(migratedData)
      
      if (originalStr === migratedStr) {
        return true
      }
      
      // state wrapperを考慮した比較
      if (originalData.state && migratedData.state) {
        return JSON.stringify(originalData.state) === JSON.stringify(migratedData.state)
      }
      
      // 主要なプロパティの存在確認
      const checkProperties = [
        'savedCharacters',
        'savedPlots',
        'savedNames',
        'relationships',
        'savedWorlds',
        'savedEnemies',
        'savedTitles',
        'savedWikipedias',
        'items',
        'savedPrompts',
        'apiKeys'
      ]
      
      for (const prop of checkProperties) {
        if (originalData[prop] || originalData.state?.[prop]) {
          const origValue = originalData[prop] || originalData.state[prop]
          const migrValue = migratedData[prop] || migratedData.state?.[prop]
          
          if (origValue && !migrValue) {
            console.error(`❌ Missing property after migration: ${prop}`)
            return false
          }
          
          if (Array.isArray(origValue)) {
            if (origValue.length !== migrValue?.length) {
              console.error(`❌ Array length mismatch for ${prop}`)
              return false
            }
          }
        }
      }
      
      return true
    } catch (error) {
      console.error('Data integrity check failed:', error)
      return false
    }
  }
  
  /**
   * 単一ストアの移行を実行
   */
  async migrateStore(storeName: string): Promise<StorageMigrationResult> {
    const plan = this.migrationPlans.get(storeName)
    
    if (!plan) {
      throw new Error(`No migration plan found for ${storeName}. Run prepareMigration first.`)
    }
    
    if (plan.status === 'completed') {
      return {
        success: true,
        storeName,
        dataIntegrity: true,
        backupKey: `${this.backupPrefix}${storeName}_*`,
        error: 'Already migrated'
      }
    }
    
    plan.status = 'in_progress'
    
    try {
      console.log(`🔄 Starting migration for ${storeName}...`)
      
      // 1. LocalStorageからデータを取得
      const localData = localStorage.getItem(plan.localStorageKey)
      if (!localData) {
        throw new Error('Local data not found')
      }
      
      const parsedData = JSON.parse(localData)
      
      // 2. IndexedDBストレージを作成
      const storage = indexedDBStorage.createStorage(storeName)
      
      // 3. IndexedDBに書き込み
      await storage.setItem(storeName, parsedData)
      
      // 4. 書き込み確認
      const migratedData = await storage.getItem(storeName)
      
      if (!migratedData) {
        throw new Error('Failed to write to IndexedDB')
      }
      
      // 5. データ整合性チェック
      const integrity = await this.verifyDataIntegrity(parsedData, migratedData)
      
      if (!integrity) {
        throw new Error('Data integrity check failed')
      }
      
      // 6. 成功時のみLocalStorageから削除（コメントアウトして段階的に）
      // localStorage.removeItem(plan.localStorageKey)
      
      plan.status = 'completed'
      
      console.log(`✅ Migration completed for ${storeName}`)
      
      return {
        success: true,
        storeName,
        dataIntegrity: integrity,
        backupKey: `${this.backupPrefix}${storeName}_*`
      }
      
    } catch (error) {
      plan.status = 'failed'
      plan.error = String(error)
      
      console.error(`❌ Migration failed for ${storeName}:`, error)
      
      return {
        success: false,
        storeName,
        dataIntegrity: false,
        backupKey: `${this.backupPrefix}${storeName}_*`,
        error: String(error)
      }
    }
  }
  
  /**
   * 移行のロールバック
   */
  async rollbackMigration(storeName: string): Promise<boolean> {
    try {
      console.log(`🔄 Rolling back migration for ${storeName}...`)
      
      // バックアップを探す
      const backups: { key: string; timestamp: number }[] = []
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith(`${this.backupPrefix}${storeName}_`)) {
          const timestamp = parseInt(key.split('_').pop() || '0')
          backups.push({ key, timestamp })
        }
      }
      
      if (backups.length === 0) {
        console.error('No backup found for rollback')
        return false
      }
      
      // 最新のバックアップを使用
      backups.sort((a, b) => b.timestamp - a.timestamp)
      const latestBackup = backups[0]
      
      const backupData = localStorage.getItem(latestBackup.key)
      if (!backupData) {
        console.error('Backup data is empty')
        return false
      }
      
      // LocalStorageに復元
      localStorage.setItem(storeName, backupData)
      
      // プランのステータスを更新
      const plan = this.migrationPlans.get(storeName)
      if (plan) {
        plan.status = 'rolled_back'
      }
      
      console.log(`✅ Rollback completed for ${storeName}`)
      return true
      
    } catch (error) {
      console.error(`❌ Rollback failed for ${storeName}:`, error)
      return false
    }
  }
  
  /**
   * 全移行計画の状態を取得
   */
  getMigrationStatus(): MigrationPlan[] {
    return Array.from(this.migrationPlans.values())
  }
  
  /**
   * バックアップをクリーンアップ（30日以上前のもの）
   */
  cleanupOldBackups(): number {
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000)
    let cleaned = 0
    
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith(this.backupPrefix)) {
        const timestamp = parseInt(key.split('_').pop() || '0')
        if (timestamp < thirtyDaysAgo) {
          localStorage.removeItem(key)
          cleaned++
        }
      }
    }
    
    if (cleaned > 0) {
      console.log(`🧹 Cleaned up ${cleaned} old backup(s)`)
    }
    
    return cleaned
  }
}

// シングルトンインスタンス
export const migrationManager = new SafeMigrationManager()